import type { ResolvedCheckpoint } from '../../logic/checkpointUtils'
import styles from './RmDotGrid.module.css'

interface RmDotGridProps {
  progress: number
  pace: number
  goal: number
  checkpoints: ResolvedCheckpoint[]
  currentCheckpointId?: string
  columns?: number
}

const PITCH = 10
const DOT_RADIUS = 3.5
const CHECKPOINT_RADIUS = 4
const PACE_RING_RADIUS = 4.6

const GATE_END_ARM = 3

function dotFill(index: number, earned: number) {
  if (index < earned) return 'var(--orange)'
  if (index === earned) return 'var(--orange-a40)'
  return 'var(--track)'
}

function gateEndPath(cx: number, cy: number) {
  return `M ${cx - GATE_END_ARM} ${cy - GATE_END_ARM} L ${cx + GATE_END_ARM} ${cy + GATE_END_ARM} M ${cx + GATE_END_ARM} ${cy - GATE_END_ARM} L ${cx - GATE_END_ARM} ${cy + GATE_END_ARM}`
}

function starPath(cx: number, cy: number) {
  const outer = 3.6
  const inner = 1.45
  const points = Array.from({ length: 10 }, (_, point) => {
    const radius = point % 2 === 0 ? outer : inner
    const angle = -Math.PI / 2 + (point * Math.PI) / 5
    return `${cx + Math.cos(angle) * radius} ${cy + Math.sin(angle) * radius}`
  })
  return `M ${points.join(' L ')} Z`
}

function dotCenter(index: number, columns: number) {
  return {
    cx: (index % columns) * PITCH + PITCH / 2,
    cy: Math.floor(index / columns) * PITCH + PITCH / 2,
  }
}

function dividerPath(threshold: number, columns: number) {
  const row = Math.floor((threshold - 1) / columns)
  const col = (threshold - 1) % columns
  const width = columns * PITCH
  const below = (row + 1) * PITCH
  if (col === columns - 1) return `M 0 ${below} H ${width}`
  return `M 0 ${below} H ${(col + 1) * PITCH} V ${row * PITCH} H ${width}`
}

function LegendSwatch({ children }: { children: React.ReactNode }) {
  return (
    <svg viewBox={`0 0 ${PITCH} ${PITCH}`} className={styles.swatch} aria-hidden>
      {children}
    </svg>
  )
}

export default function RmDotGrid({
  progress,
  pace,
  goal,
  checkpoints,
  currentCheckpointId,
  columns = 25,
}: RmDotGridProps) {
  const earned = Math.floor(Math.min(progress, goal))
  const paced = Math.round(Math.max(0, Math.min(pace, goal)))
  const rows = Math.ceil(goal / columns)
  const checkpointAt = new Map(
    checkpoints.filter((cp) => cp.rms > 0 && cp.rms <= goal).map((cp) => [cp.rms - 1, cp]),
  )
  const dividers = checkpoints.filter((cp) => cp.rms > 0 && cp.rms < goal)

  return (
    <>
      <svg
        viewBox={`0 0 ${columns * PITCH} ${rows * PITCH}`}
        className={styles.grid}
        role="img"
        aria-label={`${earned.toLocaleString()} of ${goal.toLocaleString()} RMs, pace setter at ${paced.toLocaleString()}`}
        data-testid="rm-dot-grid"
      >
        <g data-testid="rm-dots">
          {Array.from({ length: goal }, (_, index) => {
            const checkpoint = checkpointAt.get(index)
            const center = dotCenter(index, columns)
            if (checkpoint?.isFinish) {
              return (
                <path
                  key={index}
                  d={starPath(center.cx, center.cy)}
                  className={styles.finishStar}
                  data-testid="finish-star"
                >
                  <title>{`${checkpoint.city} · ${checkpoint.rms.toLocaleString()} RMs`}</title>
                </path>
              )
            }
            if (checkpoint && checkpoint.id === currentCheckpointId) {
              return (
                <path
                  key={index}
                  d={gateEndPath(center.cx, center.cy)}
                  className={styles.gateEnd}
                  data-testid="gate-end"
                >
                  <title>{`${checkpoint.city} · ${checkpoint.rms.toLocaleString()} RMs`}</title>
                </path>
              )
            }
            return (
              <circle
                key={index}
                {...center}
                r={checkpoint ? CHECKPOINT_RADIUS : DOT_RADIUS}
                fill={checkpoint ? 'var(--blue)' : dotFill(index, earned)}
              >
                {checkpoint && <title>{`${checkpoint.city} · ${checkpoint.rms.toLocaleString()} RMs`}</title>}
              </circle>
            )
          })}
        </g>
        {dividers.map((cp) => (
          <path key={cp.id} d={dividerPath(cp.rms, columns)} className={styles.divider} />
        ))}
        {paced > 0 && (
          <circle
            {...dotCenter(paced - 1, columns)}
            r={PACE_RING_RADIUS}
            className={styles.paceSetter}
            data-testid="pace-setter"
          >
            <title>{`Pace Setter · ${paced.toLocaleString()} RMs`}</title>
          </circle>
        )}
      </svg>
      <ul className={styles.legend} aria-hidden>
        <li>
          <LegendSwatch>
            <circle cx={PITCH / 2} cy={PITCH / 2} r={DOT_RADIUS} fill="var(--orange)" />
          </LegendSwatch>
          Team RMs
        </li>
        <li>
          <LegendSwatch>
            <circle cx={PITCH / 2} cy={PITCH / 2} r={PACE_RING_RADIUS - 0.5} className={styles.paceSetter} />
          </LegendSwatch>
          Pace Setter
        </li>
        <li>
          <LegendSwatch>
            <path d={`M 0 ${PITCH / 2} H ${PITCH}`} className={styles.divider} />
          </LegendSwatch>
          Gate
        </li>
      </ul>
    </>
  )
}
